import { array, option, record } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { keyBy } from 'lodash/fp';
import { Restriction, RestrictionResult } from '@mews-commander/data-access';
import { Hour, TimeUnit } from '@mews/enums';
import { createTimeUnitRangeArray, dateTimeToDayName, dateTimeToUtc } from '@mews-commander/utils';
import { Dependencies, RestrictionCalendarRow } from '../types';
import { DateRange, calculateMaxExceptionsCountOfRow } from '../selectors';
import createSortedRowConditions from './createSortedRowConditions';
import matchesDateAndRowConditions from './matchesDateAndRowConditions';

export const getOpenHoursIntervals = (restrictions: Restriction[]): number[][] => {
    const restrictedHours = [
        ...new Set(
            restrictions
                .map(({ conditions }) =>
                    conditions.hours
                        ? pipe(
                              record.toArray(conditions.hours),
                              array.filterMap(([key, value]) => (value ? option.some(Hour[key]) : option.none))
                          )
                        : []
                )
                .flat()
                .sort((a, b) => a - b)
        ),
    ];

    return restrictedHours
        .map((currentHour, i, arr) => {
            const prevHour = arr[i - 1];

            if (i === 0 && currentHour > 0) {
                return [0, currentHour];
            }

            if (i + 1 === arr.length && currentHour <= 22) {
                return [currentHour + 1, 0];
            }

            if (currentHour - prevHour > 1) {
                return [prevHour + 1, currentHour];
            }

            return null;
        })
        .filter((interval): interval is NonNullable<typeof interval> => !!interval);
};

export const getRestrictionCalendar = (
    dateRange: DateRange,
    restrictionResult: RestrictionResult,
    timeUnit: TimeUnit
): Array<RestrictionCalendarRow> => {
    const { restrictions, resourceCategories, rateGroups, rates } = restrictionResult;

    if (!restrictions || !resourceCategories || !rateGroups || !rates) {
        return [];
    }
    const restrictionArray = Object.values(restrictions);

    const keyedRateGroups = keyBy('id', rateGroups);
    const keyedRates = keyBy('id', rates);
    const keyedResourceCategories = keyBy('id', resourceCategories);

    const dependencies = {
        rateGroups: keyedRateGroups,
        rates: keyedRates,
        resourceCategories: keyedResourceCategories,
    } as Dependencies;

    const rowConditionsArray = createSortedRowConditions(restrictionArray, dependencies);
    const dateRangeArray = createTimeUnitRangeArray({
        startDateTime: dateRange.startDateTime,
        endDateTime: dateRange.endDateTime,
        timeUnit: TimeUnit.Day,
    });

    const dateConditionsArray = dateRangeArray.map((dateTime) => ({
        utcDate: dateTimeToUtc(dateTime),
        day: dateTimeToDayName(dateTime) as string,
    }));

    return pipe(
        rowConditionsArray,
        array.filterMap((rowConditions) =>
            pipe(
                dateConditionsArray,
                array.map((dateConditions) =>
                    pipe(
                        restrictionArray,
                        array.filter(matchesDateAndRowConditions(rowConditions, dateConditions, dependencies))
                    )
                ),
                option.fromPredicate(array.some(array.isNonEmpty)),
                option.map((rowRestrictions) => ({
                    conditions: rowConditions,
                    restrictions: rowRestrictions,
                    maxExceptionsCount: calculateMaxExceptionsCountOfRow(rowRestrictions, timeUnit),
                    hoursIntervalCount: Math.max(...rowRestrictions.map(flow(getOpenHoursIntervals, array.size))),
                }))
            )
        )
    );
};
